/**
 * Who needs follow-up: overdue patients to trace, those due today, those who promised to come,
 * those lost to follow-up, and referrals still waiting for their outcome.
 */
import {
  type FollowupState,
  type PatientBundle,
  type ServiceRequest,
  openReferrals,
  urgencyOf,
  worklist,
} from '@assist/core';
import { useEffect, useState } from 'preact/hooks';
import { loadBundles } from '../care.js';
import { clock } from '../controller.js';
import { eth } from '../dates.js';
import { go, useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';
import { displayName } from '../patient.js';

type Tab = 'overdue' | 'due' | 'promised' | 'lost' | 'referrals';

export function Followup() {
  const a = useApp();
  const [bundles, setBundles] = useState<PatientBundle[] | null>(null);
  const [tab, setTab] = useState<Tab>('overdue');
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the store changes
  useEffect(() => {
    let live = true;
    void loadBundles().then((b) => live && setBundles(b));
    return () => {
      live = false;
    };
  }, [a.status]);
  const packs = activePacks(a.packSettings).map((x) => x.pack);
  const today = clock.today();
  if (!packs.length)
    return (
      <div class="card">
        <h3>{t.followupTitle}</h3>
        <p class="muted">{t.noPacks}</p>
      </div>
    );
  if (!bundles) return <p class="muted">{t.computing}</p>;

  const entries = worklist(bundles, packs, today);
  const byState = (s: FollowupState) => entries.filter((e) => e.status.state === s);
  const referrals: Array<{ sr: ServiceRequest; b: PatientBundle }> = bundles.flatMap((b) =>
    openReferrals(b.record.referrals).map((sr) => ({ sr, b })),
  );
  const programmeName = (code: string) =>
    packs.flatMap((p) => p.programmes).find((p) => p.code === code)?.display ?? code;
  const tabs: Array<[Tab, string, number]> = [
    ['overdue', t.tabOverdue, byState('overdue').length],
    ['due', t.tabDue, byState('due').length],
    ['promised', t.tabPromised, byState('promised').length],
    ['lost', t.tabLost, byState('lost').length],
    ['referrals', t.tabReferrals, referrals.length],
  ];

  return (
    <div class="card">
      <h3>{t.followupTitle}</h3>
      <div class="tabs" role="tablist">
        {tabs.map(([k, label, n]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            class={tab === k ? 'on' : ''}
            onClick={() => setTab(k)}
          >
            {label} <span class="count">{n}</span>
          </button>
        ))}
      </div>
      {tab === 'referrals' ? (
        referrals.length ? (
          <ul class="list worklist">
            {referrals.map(({ sr, b }) => (
              <li key={sr.id}>
                <a href={`#/patient/${b.patient.id}`}>
                  <div>
                    <strong>{displayName(b.patient)}</strong>
                    {urgencyOf(sr) !== 'routine' ? (
                      <span class="pill red">{t.urgencies[urgencyOf(sr)]}</span>
                    ) : null}
                  </div>
                  <div class="muted">
                    {eth(sr.authoredOn)} · {sr.reasonCode?.[0]?.text ?? sr.code.text}
                  </div>
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p class="empty">{t.emptyList}</p>
        )
      ) : (
        <WorkRows rows={byState(tab)} programmeName={programmeName} />
      )}
    </div>
  );
}

function WorkRows({
  rows,
  programmeName,
}: {
  rows: ReturnType<typeof worklist>;
  programmeName: (c: string) => string;
}) {
  if (!rows.length) return <p class="empty">{t.emptyList}</p>;
  return (
    <ul class="list worklist">
      {rows.map(({ patient, status, programmes }) => {
        const phone = patient.telecom?.find((x) => x.system === 'phone')?.value;
        const last = status.attempts[0];
        return (
          <li key={patient.id} data-patient={patient.id}>
            <div class="row-main">
              <button type="button" class="link name" onClick={() => go(`patient/${patient.id}`)}>
                {displayName(patient)}
              </button>
              <div class="muted">{programmes.map(programmeName).join(', ')}</div>
              <div>
                {status.state === 'overdue' ? (
                  <strong class="late">{t.daysLate(status.daysLate)}</strong>
                ) : status.state === 'promised' && status.pausedUntil ? (
                  t.comesOn(eth(status.pausedUntil))
                ) : status.state === 'lost' ? (
                  t.noVisitSince(eth(status.lastVisit ?? status.enrolledOn))
                ) : (
                  t.dueOn(eth(status.dueDate))
                )}
                {last ? (
                  <span class="muted">
                    {' '}
                    · {t.attemptsN(status.attempts.length)}: {t.outcomes[last.outcome ?? ''] ?? '—'}
                  </span>
                ) : null}
              </div>
            </div>
            {phone ? (
              <a class="sec call" href={`tel:${phone.replace(/\s/g, '')}`}>
                {phone}
              </a>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
